import axios from 'axios';
import { envConstants } from './constants';
import { fetchAuthSession } from 'aws-amplify/auth';


const BASE_API_URL = envConstants.BASE_API_URL;

/**
 * 
 * @returns Fetchs token from the session
 */
 const fetchAccessToken =async ()=>{
    const session = await fetchAuthSession();
    return session?.tokens?.accessToken.toString();
}

/* interceptor for axios to add access token to all requests */
axios.interceptors.request.use(
  async (config) => {
    const accessToken = await fetchAccessToken();
    config.headers.Authorization = `Bearer ${accessToken}`;
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

/* Response interceptor to handle authentication errors */
axios.interceptors.response.use(
  response => response,
  error => {
    // If we get a 401 Unauthorized error, the token has expired
    if (error.response?.status === 401) {
      console.error('Authentication error: Token expired or invalid');
      // Redirect to login page
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Fetch all customers
export const fetchCustomers = async (compId, currRecInd, custId = null) => {
  if(!compId) {
    throw new Error('companyId is required');
  }
  try {
    const response = await axios.get(`${BASE_API_URL}/customer`, {
      params: { compId, currRecInd, custId }
    });
    return response.data.data; 
  } catch (error) {
    console.error('Error fetching customers:', error);
    throw error;
  }
};

// Fetch an evidence image's bytes through the backend proxy. Used instead of fetching the
// S3 URL directly because the bucket has no CORS rule allowing this origin to read the
// response body (viewing the URL directly in a tab/img still works fine).
export const fetchImageBlob = async (imageUrl) => {
  const response = await axios.get(`${BASE_API_URL}/images/proxy`, {
    params: { url: imageUrl },
    responseType: 'blob',
  });
  return response.data;
};

//Fetch Pool id by customer
export const fetchPoolByCustomer = async (companyId, custId) => {
  if(!companyId) {
    throw new Error('companyId is required');
  }
  try {
    const response = await axios.get(`${BASE_API_URL}/pool-details`, {
      params: { companyId, custId }
    });
    return response.data.data; 
  }
  catch (error) {
    console.error('Error fetching pool:', error);
    throw error;
  }
}

// Fetch all employees
export const fetchEmployees = async (compId, empId = null, dsgn = null) => {
  // if (!compId) {
  //   throw new Error("companyId is required");
  // }
  try {
    let token = await fetchAccessToken();
    const response = await axios.get(`${BASE_API_URL}/employee`, {
      params: { compId, empId, dsgn },
      headers:{
        Authorization: 'Bearer '+token,
      }
    });

    return response.data.data;
  } catch (error) {
    console.error("Error fetching employees:", error);
    throw error;
  }
};

// Fetch work order (task) by date
export const fetchDailyWorkOrders = async (companyId, taskDate) => {
  try {
    const response = await axios.get(`${BASE_API_URL}/work-order`, {
      params: { companyId, taskDate },
    });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching work order by date:", error);
    throw error;
  }
};

// Post Work Order
export const postWorkOrder = async (workOrderData) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/work-order`, workOrderData);
    return response.data.data;
  } catch (error) {
    console.error("Error posting work order:", error);
    throw error;
  }
};

// Post Customer Order
export const postCustomerOrder = async (customerData) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/customer`, customerData);
    return response.data.data;
  } catch (error) {
    console.error("Error posting work order:", error);
    throw error;
  }
};

// PUT call for work order
export const updateWorkOrder = async (updatedData) => {
  try {
    // console.log("Sending PUT request to:", `${BASE_API_URL}/work-order`);
    // console.log("Payload:", updatedData);
    const response = await axios.put(`${BASE_API_URL}/work-order`, updatedData);
    // console.log("Work order updated successfully:", response.data);
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Server error:", error.response.status, error.response.data);
    } else {
      console.error("Request error:", error.message);
    }
    throw error;
  }
};
// Fetch all work orders
export const fetchAllWorkOrders = async (companyId ) => {
  try {
      const response = await axios.get(`${BASE_API_URL}/work-order`, {
          params: { companyId },
      });
      return response.data.data;
  } catch (error) {
      console.error("Error fetching all work orders:", error);
      throw error;
  }
};

/**
 * Filter work orders to only include those for active customers
 * This requires passing in the list of active customer IDs
 * @param {Array} workOrders - Array of work order objects
 * @param {Array} activeCustomerIds - Array of active customer IDs or Set
 * @returns {Array} - Filtered array of work orders for active customers only
 */
export const filterWorkOrdersForActiveCustomers = (workOrders, activeCustomerIds) => {
  if (!Array.isArray(workOrders)) return [];

  // Convert to Set for O(1) lookup if it's an array
  const activeIds = activeCustomerIds instanceof Set
    ? activeCustomerIds
    : new Set(activeCustomerIds);

  return workOrders.filter(wo => {
    if (!wo.workOrder) return false;

    const customerId = wo.workOrder.customerId;
    const isActive = activeIds.has(customerId);

    if (!isActive) {
      console.log(`Filtering out work order for inactive customer ID: ${customerId}`);
    }

    return isActive;
  });
};


// Fetch tasks (by date)
export const fetchTasksByDate = async (companyId, taskDate) => {
  try {
    const response = await axios.get(`${BASE_API_URL}/tasks`, {
      params: { companyId, taskDate },
    });
    return response.data.data;
  }
  catch (error) {
    console.error("Error fetching tasks:", error);
    throw error;
  }
};

/**
 * Filter tasks to only include those for active customers
 * @param {Array} tasks - Array of task objects with embedded customer data
 * @returns {Array} - Filtered array of tasks for active customers only
 */
export const filterTasksForActiveCustomers = (tasks) => {
  if (!Array.isArray(tasks)) return [];

  return tasks.filter(task => {
    // Check if customer data exists and customer is active (currRecInd = 'Y')
    if (task.customer && task.customer.currRecInd === 'Y') {
      return true;
    }
    // Log filtered out tasks for debugging
    if (task.customer && task.customer.currRecInd === 'N') {
      console.log(`Filtering out task for inactive customer: ${task.customer.custFirstName} ${task.customer.custLastName} (ID: ${task.customer.customerId})`);
    }
    return false;
  });
};

// Fetch all tasks
export const fetchAllTasks = async (companyId) => {
  try {
    const response = await axios.get(`${BASE_API_URL}/tasks`, {
      params: { companyId },
    });
    return response.data.data;
  }
  catch (error) {
    console.error("Error fetching all tasks:", error);
    throw error;
  }
};

// Persist a route's visit order onto the recurrence rules (survives all future occurrences).
// orderedWorkOrderIds = the day's stops in their new sequence.
export const reorderRoute = async (orderedWorkOrderIds) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/routes/reorder`, { orderedWorkOrderIds });
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Server error:", error.response.status, error.response.data);
    } else {
      console.error("Request error:", error.message);
    }
    throw error;
  }
};

// PUT call for task
export const updateTask = async (updatedData) => {
  try {
    // console.log("Sending PUT request to:", `${BASE_API_URL}/tasks`);
    // console.log("Payload:", updatedData);
    const response = await axios.put(`${BASE_API_URL}/tasks`, updatedData);
    // console.log("Task updated successfully:", response.data);
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Server error:", error.response.status, error.response.data);
    } else {
      console.error("Request error:", error.message);
    }
    throw error;
  }
};

// New function for bulk upload
export const postBulkUpload = async (file, companyId) => {
  try {
    console.log("Bulk upload starting...");
    console.log("File:", file);
    console.log("File name:", file.name);
    console.log("File size:", file.size, "bytes");
    console.log("File type:", file.type);
    console.log("Company ID:", companyId);

    const formData = new FormData();
    formData.append("files", file);  // Changed from "file" to "files" to match backend
    formData.append("companyId", companyId);
    let token = await fetchAccessToken()

    console.log("Sending POST request to:", `${BASE_API_URL}/upload`);

    const response = await axios.post(`${BASE_API_URL}/upload`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: 'Bearer '+token,
      },
    });
    console.log("Upload successful:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error during bulk upload:", error);
    console.error("Error status:", error.response?.status);
    console.error("Error data:", error.response?.data);
    console.error("Error headers:", error.response?.headers);
    throw error;
  }
};
// Post Customer Order
export const postEmployeeOrder = async (employeeData) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/employee`, employeeData);
    return response.data.data;
  } catch (error) {
    console.error("Error posting work order:", error);
    throw error;
  }
};

// PUT call for employees
export const editEmployee = async (employeeData) => {
  try {
    const response = await axios.put(`${BASE_API_URL}/employee`, employeeData);
    return response;
  }
  catch (error) {
    console.error("Error updating Employee details: ", error);
    throw error;
  }
}

// DELETE call for employee
export const deleteEmployee = async (companyId, employeeId) => {
  try {
    const response = await axios.delete(`${BASE_API_URL}/employee`, {
      params: { compId: companyId, empId: employeeId }
    });
    return response;
  }
  catch (error) {
    console.error("Error deleting Employee: ", error);
    throw error;
  }
}

export const updateCustomer = async (customerData, custId) => {
  try {
    const response = await axios.put(`${BASE_API_URL}/customer/${custId}`, customerData);
    return response;
  }
  catch (error) {
    console.error("Error updating Customer details: ", error);
    throw error;
  }
}

// DELETE call for customer (permanent deletion)
export const deleteCustomer = async (customerId, companyId) => {
  try {
    const response = await axios.delete(`${BASE_API_URL}/customer`, {
      params: { custId: customerId, compId: companyId }
    });
    return response;
  }
  catch (error) {
    console.error("Error deleting Customer: ", error);
    throw error;
  }
}

// Post company Order
export const postCompany = async (comapnyData) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/company`, comapnyData);
    return response.data.data;
  } catch (error) {
    console.error("Error posting company details:", error);
    throw error;
  }
};

// Fetch monthly payment details
export const fetchMonthlyPayment = async (companyId) => {
  try {
    const response = await axios.get(`${BASE_API_URL}/subscriptions/${companyId}`);
    return response.data.data;
  } catch (error) {
    console.error("Error fetching monthly payment:", error);
    throw error;
  }
};


export const createCheckoutSession = async (requestSessionData) => {
  try{
    const response = await axios.post(`${BASE_API_URL}/subscriptions/payments/session`, requestSessionData);
    return response;
  } catch(error) {
    console.error("Error creating checkout session: ", error);
    throw error;
  }
}

export const fetchTaskRecords = async ({
  companyId,
  startDate,
  endDate,
  customerId,
  employeeId,
}) => {
  if (!companyId || !startDate || !endDate) {
    return [];
  }

  const params = {
    companyId,          
    compId: companyId,  
    startDate,
    endDate,
    limit: 10000,
  };

  if (customerId) params.customerId = customerId;
  if (employeeId) params.employeeId = employeeId;

  try {
    const response = await axios.get(`${BASE_API_URL}/logs`, { params });

    const rawLogs = response.data.data || [];

    return rawLogs.map((log) => ({
      taskId: log.taskId,
      taskDetails: {
        ...log,
      },
      customer: {
        customerId: log.customerId,
      },
      employee: {
        empId: log.empId,
      },
    }));
  } catch (error) {
    console.error("Error fetching task records:", error);
    throw error;
  }
};


export const verifyCheckoutSession = async (sessionId) => {
  try {
    const response = await axios.get(`${BASE_API_URL}/subscriptions/payments/session/${sessionId}`);
    return response.data.data;
  } catch (error) {
    console.error("Error verifying checkout session: ", error);
    throw error;
  }
}

// Fetch invoices for billing history
export const fetchInvoices = async (companyId, limit = null) => {
  try {
    const params = limit ? { limit } : {};
    const response = await axios.get(`${BASE_API_URL}/subscriptions/${companyId}/invoices`, { params });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching invoices:", error);
    throw error;
  }
}

// Set default payment method
export const setDefaultPaymentMethod = async (companyId, paymentMethodId) => {
  try {
    const response = await axios.put(`${BASE_API_URL}/subscriptions/${companyId}/payment-methods/${paymentMethodId}/default`);
    return response.data;
  } catch (error) {
    console.error("Error setting default payment method:", error);
    throw error;
  }
}

// Delete payment method
export const deletePaymentMethod = async (companyId, paymentMethodId) => {
  try {
    const response = await axios.delete(`${BASE_API_URL}/subscriptions/${companyId}/payment-methods/${paymentMethodId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting payment method:", error);
    throw error;
  }
}

// Fetch access roles of application
export const fetchAccessRoles = async () => {
  // if (!compId) {
  //   throw new Error("companyId is required");
  // }
  try {
    let token = await fetchAccessToken();
    const response = await axios.get(`${BASE_API_URL}/roles`, {
      headers:{
        Authorization: 'Bearer '+token,
      }
    });

    return response.data.data;
  } catch (error) {
    console.error("Error fetching roles:", error);
    throw error;
  }
};

// Update which sections appear in the service completion email sent to customers
export const updateCompanyEmailSettings = async (companyId, settings) => {
  try {
    const response = await axios.patch(`${BASE_API_URL}/company/${companyId}/email-settings`, settings);
    return response.data.data;
  } catch (error) {
    console.error('Error updating email settings:', error);
    throw error;
  }
};

// Fetch all companies or specific company by ID
export const fetchCompanies = async (companyId = null) => {
  try {
    let token = await fetchAccessToken();
    const response = await axios.get(`${BASE_API_URL}/company`, {
      params: companyId ? { id: companyId } : {},
      headers:{
        Authorization: 'Bearer '+token,
      }
    });

    return response.data.data;
  } catch (error) {
    console.error("Error fetching companies:", error);
    throw error;
  }
};

// Fetch all companies with their employees and customers for Company View
export const fetchCompanyView = async () => {
  try {
    let token = await fetchAccessToken();
    const response = await axios.get(`${BASE_API_URL}/company/view`, {
      headers:{
        Authorization: 'Bearer '+token,
      }
    });

    return response.data.data;
  } catch (error) {
    console.error("Error fetching company view:", error);
    throw error;
  }
};

// ----------------------------------------------------------------
// Schedule Templates
// ----------------------------------------------------------------

export const createTemplate = async (templateData) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/templates`, templateData);
    return response.data.data;
  } catch (error) {
    console.error("Error creating template:", error);
    throw error;
  }
};

export const fetchTemplates = async (companyId, employeeId) => {
  try {
    const response = await axios.get(`${BASE_API_URL}/templates`, {
      params: { companyId, employeeId },
    });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching templates:", error);
    throw error;
  }
};

export const fetchTemplateById = async (templateId, companyId) => {
  try {
    const response = await axios.get(`${BASE_API_URL}/templates/${templateId}`, {
      params: { companyId },
    });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching template:", error);
    throw error;
  }
};

export const updateTemplate = async (templateId, templateData) => {
  try {
    const response = await axios.put(`${BASE_API_URL}/templates/${templateId}`, templateData);
    return response.data.data;
  } catch (error) {
    console.error("Error updating template:", error);
    throw error;
  }
};

export const deleteTemplate = async (templateId, companyId) => {
  try {
    const response = await axios.delete(`${BASE_API_URL}/templates/${templateId}`, {
      params: { companyId },
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting template:", error);
    throw error;
  }
};

// Snapshot the live schedule into a template (WEEK / COMPANY_DAY / COMPANY_WEEK),
// or take an automatic recovery snapshot. Built server-side from active work orders.
export const snapshotSchedule = async (snapshotData) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/templates/snapshot`, snapshotData);
    return response.data.data;
  } catch (error) {
    console.error("Error snapshotting schedule:", error);
    throw error;
  }
};

// Returns today's local date as "YYYY-MM-DD" using the user's device clock.
// Passed to backend to prevent UTC-midnight timezone shift (e.g. 11 PM Texas = next day UTC).
const getLocalDateString = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Restore (apply) a saved template onto live schedules — the backup-recovery path.
// request: { companyId, conflictMode: "OVERWRITE"|"MERGE", targetEmployeeId?, targetDayOfWeek?, forceThisWeek? }
export const applyTemplate = async (templateId, request) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/templates/${templateId}/apply`, {
      ...request,
      clientDate: getLocalDateString(),
    });
    return response.data.data;
  } catch (error) {
    console.error("Error applying template:", error);
    throw error;
  }
};

// Dry-run preview of a restore — returns the per-route delta without persisting.
// Same request body as applyTemplate. Used to show a confirmation diff first.
export const previewApplyTemplate = async (templateId, request) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/templates/${templateId}/apply/preview`, {
      ...request,
      clientDate: getLocalDateString(),
    });
    return response.data.data;
  } catch (error) {
    console.error("Error previewing template restore:", error);
    throw error;
  }
};

// Apply an explicit, user-edited set of routes (the "customize before confirm" path).
// request: { companyId, forceThisWeek?, routes: [{ employeeId, dayOfWeek, stops:[{customerId, poolId}] }] }
export const applyRoutes = async (request) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/templates/apply-routes`, {
      ...request,
      clientDate: getLocalDateString(),
    });
    return response.data.data;
  } catch (error) {
    console.error("Error applying edited routes:", error);
    throw error;
  }
};

// Trigger the weekly task generation job on-demand.
// Creates missing tasks for all active work orders whose next occurrence
// falls within the next 7 days. Fixes stale schedules without waiting for Sunday.
export const generateTasks = async () => {
  try {
    const response = await axios.post(`${BASE_API_URL}/tasks/generate`);
    return response.data;
  } catch (error) {
    console.error("Error triggering task generation:", error);
    throw error;
  }
};

// Gate Code API functions
export const fetchGateCodes = async (customerId) => {
  try {
    const response = await axios.get(`${BASE_API_URL}/customer/${customerId}/gate-codes`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching gate codes:', error);
    throw error;
  }
};

export const createGateCode = async (customerId, gateCodeData) => {
  try {
    const response = await axios.post(`${BASE_API_URL}/customer/${customerId}/gate-codes`, gateCodeData);
    return response.data.data;
  } catch (error) {
    console.error('Error creating gate code:', error);
    throw error;
  }
};

export const updateGateCode = async (customerId, gateCodeId, gateCodeData) => {
  try {
    const response = await axios.put(`${BASE_API_URL}/customer/${customerId}/gate-codes/${gateCodeId}`, gateCodeData);
    return response.data.data;
  } catch (error) {
    console.error('Error updating gate code:', error);
    throw error;
  }
};

export const deleteGateCode = async (customerId, gateCodeId) => {
  try {
    await axios.delete(`${BASE_API_URL}/customer/${customerId}/gate-codes/${gateCodeId}`);
  } catch (error) {
    console.error('Error deleting gate code:', error);
    throw error;
  }
};
